/*
 * taskpane-auth.ts — Taskpane-specific authentication UI and lifecycle management.
 *
 * Core auth state and token acquisition logic is delegated to shared/auth-state.ts.
 * This module adds DOM/UI concerns: diagnostics rendering, token maintenance timers,
 * sign-in/sign-out button handlers, and auth banner management.
 */

/* global document, localStorage, sessionStorage, window, navigator, Office */

import { AuthenticationResult, BrowserCacheLocation } from "@azure/msal-browser";
import { buildAuthRedirectUri } from "../shared/auth";
import {
  STORAGE_KEY_BC_URL,
  STORAGE_KEY_CLIENT_ID,
  STORAGE_KEY_COMPANY_ID,
  STORAGE_KEY_COMPANY_NM,
  STORAGE_KEY_COMPANY_ACRONYM,
  STORAGE_KEY_DEPARTMENT_SET_ENTRY_ID_FLT,
  Settings,
} from "../shared/settings";
import { showCompanySection, showCustomersSection } from "./helpers";

import {
  ensureRuntimeConfigLoaded,
  loadSettings as loadSettingsCore,
  getMsalInstance,
  ensureMsalInitialized,
  acquireBcToken,
  ensureAccessToken as ensureAccessTokenCore,
  applyTokenToState,
  getBcConnectionContext,
  getBcRequestContext,
  resetMsalState,
  clearMsalStorageEntries,
  getUseTaskpaneConfiguration,
  getShowDiagnosticsSection,
  getRuntimeConfigSource,
  getRuntimeConfigLastError,
  getRuntimeConfigLoadedAtMs,
  getLastTokenSource,
  getLastAuthError,
  getMsalCacheLocation,
  getCachedToken,
  getTokenExpiresAtMs,
  getCurrentAccount,
  getMsalInstanceRef,
  onRuntimeConfigReloaded,
} from "../shared/auth-state";

// Re-export public API for consumers of taskpane-auth
export { ensureRuntimeConfigLoaded, getBcConnectionContext, getBcRequestContext };
export { loadSettingsCore as loadSettings };

// ─── Constants ──────────────────────────────

const STORAGE_KEY_LAST_SIGNED_IN_USER = "bcaddin_last_signed_in_user";
const TOKEN_REFRESH_LEAD_MS = 5 * 60 * 1000;
const TOKEN_EXPIRY_NOTICE_MS = 2 * 60 * 1000;

// ─── Module state (UI-specific) ─────────────

let tokenRefreshTimerId: number | null = null;
let tokenExpiryNoticeTimerId: number | null = null;
let tokenRefreshScheduledAtMs = 0;

// ─── Settings helpers ───────────────────────

export function saveSettings(s: Settings): void {
  localStorage.setItem(STORAGE_KEY_BC_URL, s.bcUrl.trim());
  localStorage.setItem(STORAGE_KEY_CLIENT_ID, s.clientId.trim());
}

export function clearStoredSettingsAndAuth(): void {
  localStorage.removeItem(STORAGE_KEY_BC_URL);
  localStorage.removeItem(STORAGE_KEY_CLIENT_ID);
  localStorage.removeItem(STORAGE_KEY_COMPANY_ID);
  localStorage.removeItem(STORAGE_KEY_COMPANY_NM);
  localStorage.removeItem(STORAGE_KEY_COMPANY_ACRONYM);
  localStorage.removeItem(STORAGE_KEY_DEPARTMENT_SET_ENTRY_ID_FLT);
  localStorage.removeItem(STORAGE_KEY_LAST_SIGNED_IN_USER);
}

// ─── Runtime config UI integration ──────────

// Register listener for runtime config changes to update UI
onRuntimeConfigReloaded((changed) => {
  if (changed.cacheLocationChanged || changed.clientIdChanged) {
    stopTokenMaintenanceTimers();
    clearAuthSessionNote();
    showAuthDetail("");
    setAuthState("none");
    updateAuthBanner(false);
    updateAuthActionButtons(false);
    showCompanySection(false);
    showCustomersSection(false);
  }
  applyRuntimeVisibility();
  renderAuthDiagnostics();
});

export function applyRuntimeVisibility(): void {
  const configurationSection = document.getElementById("configuration-section");
  if (configurationSection) configurationSection.style.display = getUseTaskpaneConfiguration() ? "block" : "none";

  const diagnosticsBox = document.getElementById("diagnostics-box");
  if (diagnosticsBox) diagnosticsBox.style.display = getShowDiagnosticsSection() ? "block" : "none";
}

// ─── Token with UI side effects ─────────────

export async function ensureAccessToken(interactive: boolean): Promise<string | null> {
  const token = await ensureAccessTokenCore(interactive);
  renderAuthDiagnostics();
  return token;
}

export function applyToken(result: AuthenticationResult): void {
  applyTokenToState(result);
  localStorage.setItem(STORAGE_KEY_LAST_SIGNED_IN_USER, result.account?.username ?? "signed-in");

  setAuthState("ok");
  showAuthDetail(`Signed in as ${result.account?.username ?? "current user"}`);
  clearAuthSessionNote();
  scheduleTokenMaintenance();
  updateAuthBanner(true);
  updateAuthActionButtons(true);
  const hasBcConfig = Boolean(loadSettingsCore().bcUrl);
  showCompanySection(hasBcConfig);
  showCustomersSection(hasBcConfig);
  renderAuthDiagnostics();
}

// ─── Sign in / out ──────────────────────────

export type SignInCallback = () => Promise<void>;
let onSignInSuccess: SignInCallback | null = null;

export function setOnSignInSuccess(callback: SignInCallback): void {
  onSignInSuccess = callback;
}

// Guards against overlapping interactive sign-in attempts -- e.g. a stray double-click
// on Sign In / Change account would otherwise try to pop two MSAL popups at once.
let interactiveSignInInFlight = false;

/**
 * Interactive sign-in. With forceAccountPicker=true (the "Change account" button), this
 * always shows the AAD account chooser, even if a cached account/token is already valid --
 * see acquireBcToken. Without it (the "Sign In" button, only visible when signed out),
 * it behaves as before: silent-first, falling back to the popup.
 */
export async function signIn(forceAccountPicker = false): Promise<void> {
  if (interactiveSignInInFlight) return;

  const msal = getMsalInstance();
  if (!msal) {
    setAuthState("error");
    showAuthDetail(
      getUseTaskpaneConfiguration()
        ? "Please enter your Azure App Client ID in settings and save first."
        : "Azure App Client ID is missing in runtime environment configuration.",
    );
    return;
  }

  interactiveSignInInFlight = true;
  try {
    await ensureMsalInitialized(msal);
    setAuthState("loading");
    try {
      const result = await acquireBcToken(true, forceAccountPicker);
      if (!result) {
        setAuthState("none");
        updateAuthBanner(false);
        updateAuthActionButtons(false);
        showCompanySection(false);
        showCustomersSection(false);
        renderAuthDiagnostics();
        return;
      }
      applyToken(result);
      if (onSignInSuccess) await onSignInSuccess();
    } catch (err: any) {
      stopTokenMaintenanceTimers();
      clearAuthSessionNote();
      setAuthState("error");
      showAuthDetail(err.message ?? "Sign-in failed.");
      updateAuthBanner(false);
      updateAuthActionButtons(false);
      showCompanySection(false);
      showCustomersSection(false);
      renderAuthDiagnostics();
    }
  } finally {
    interactiveSignInInFlight = false;
  }
}

export function signOut(): void {
  const clientId = loadSettingsCore().clientId;
  if (clientId) {
    clearMsalStorageEntries(clientId, [localStorage, sessionStorage]);
  }

  resetMsalState();
  stopTokenMaintenanceTimers();
  clearAuthSessionNote();
  localStorage.removeItem(STORAGE_KEY_LAST_SIGNED_IN_USER);
  setAuthState("none");
  showAuthDetail("");
  updateAuthBanner(false);
  updateAuthActionButtons(false);
  showCompanySection(false);
  showCustomersSection(false);
  renderAuthDiagnostics();
}

/**
 * Reset MSAL state when settings are saved (new clientId may apply).
 */
export function resetAuthState(): void {
  resetMsalState();
  setAuthState("none");
  updateAuthBanner(false);
  updateAuthActionButtons(false);
  showCompanySection(false);
  showCustomersSection(false);
  showAuthDetail("");
  renderAuthDiagnostics();
}

// ─── Auth UI helpers ────────────────────────

function setAuthState(state: "none" | "loading" | "ok" | "error"): void {
  const icon = document.getElementById("auth-status-icon");
  const text = document.getElementById("auth-status-text");
  const box = document.getElementById("auth-status-box");
  if (!icon || !text || !box) return;

  box.className = "auth-status-box";
  switch (state) {
    case "none":
      icon.innerHTML = "&#8212;";
      text.textContent = "Not signed in";
      break;
    case "loading":
      icon.innerHTML = "&#9203;";
      text.textContent = "Signing in...";
      break;
    case "ok":
      icon.innerHTML = "&#9989;";
      text.textContent = "Signed in";
      box.classList.add("auth-status-box--ok");
      break;
    case "error":
      icon.innerHTML = "&#10060;";
      text.textContent = "Sign-in failed";
      box.classList.add("auth-status-box--error");
      break;
  }
}

function showAuthDetail(msg: string): void {
  const el = document.getElementById("auth-detail");
  if (el) el.textContent = msg;
}

function updateAuthBanner(authenticated: boolean): void {
  const banner = document.getElementById("auth-banner");
  if (banner) banner.style.display = authenticated ? "none" : "flex";
  if (!authenticated) {
    // A "company failed to load" banner from before sign-out would otherwise keep
    // showing stacked under "Not authenticated" -- of course companies aren't loaded
    // once signed out, so it's redundant. Hidden by id directly (not imported from
    // companies.ts) to avoid a circular dependency between the two modules.
    const companyLoadBanner = document.getElementById("company-load-banner");
    if (companyLoadBanner) companyLoadBanner.style.display = "none";
  }
}

function updateAuthActionButtons(signedIn: boolean): void {
  const btnIn = document.getElementById("btn-sign-in");
  const btnChange = document.getElementById("btn-change-account");
  if (btnIn) btnIn.style.display = signedIn ? "none" : "inline-block";
  if (btnChange) btnChange.style.display = signedIn ? "inline-block" : "none";
}

function showAuthSessionNote(message: string): void {
  const el = document.getElementById("auth-session-note");
  if (!el) return;
  el.textContent = message;
  el.style.display = message ? "block" : "none";
}

function clearAuthSessionNote(): void {
  showAuthSessionNote("");
}

// ─── Token maintenance ──────────────────────

function stopTokenMaintenanceTimers(): void {
  if (tokenRefreshTimerId !== null) {
    window.clearTimeout(tokenRefreshTimerId);
    tokenRefreshTimerId = null;
  }
  tokenRefreshScheduledAtMs = 0;
  if (tokenExpiryNoticeTimerId !== null) {
    window.clearTimeout(tokenExpiryNoticeTimerId);
    tokenExpiryNoticeTimerId = null;
  }
}

function maybeShowSessionContinuityHint(): void {
  if (getMsalCacheLocation() !== BrowserCacheLocation.LocalStorage) return;
  const hadPriorSession = (localStorage.getItem(STORAGE_KEY_LAST_SIGNED_IN_USER) ?? "").trim().length > 0;
  if (!hadPriorSession) return;
  showAuthSessionNote("Saved session was not restored. Click Sign In to continue.");
}

function scheduleTokenMaintenance(): void {
  stopTokenMaintenanceTimers();
  const cachedToken = getCachedToken();
  const tokenExpiresAtMs = getTokenExpiresAtMs();
  if (!cachedToken || !tokenExpiresAtMs) return;

  const now = Date.now();
  const refreshDelayMs = Math.max(1_000, tokenExpiresAtMs - TOKEN_REFRESH_LEAD_MS - now);
  tokenRefreshScheduledAtMs = now + refreshDelayMs;
  tokenRefreshTimerId = window.setTimeout(() => {
    void runBackgroundTokenRefresh();
  }, refreshDelayMs);

  const noticeDelayMs = tokenExpiresAtMs - TOKEN_EXPIRY_NOTICE_MS - now;
  if (noticeDelayMs <= 0) {
    updateTokenExpiryNotice();
    return;
  }
  tokenExpiryNoticeTimerId = window.setTimeout(() => {
    updateTokenExpiryNotice();
  }, noticeDelayMs);
}

function updateTokenExpiryNotice(): void {
  const cachedToken = getCachedToken();
  const tokenExpiresAtMs = getTokenExpiresAtMs();
  if (!cachedToken || !tokenExpiresAtMs) {
    clearAuthSessionNote();
    return;
  }

  const remainingMs = tokenExpiresAtMs - Date.now();
  if (remainingMs > TOKEN_EXPIRY_NOTICE_MS) {
    clearAuthSessionNote();
    return;
  }
  if (remainingMs <= 0) {
    showAuthSessionNote("Session expired. Click Sign In to continue.");
    return;
  }

  const remainingMinutes = Math.max(1, Math.ceil(remainingMs / 60_000));
  showAuthSessionNote(`Session expires in ${remainingMinutes} minute(s). Keep pane open or click Sign In to refresh.`);
}

async function runBackgroundTokenRefresh(): Promise<void> {
  try {
    const token = await ensureAccessTokenCore(false);
    if (token) {
      clearAuthSessionNote();
      scheduleTokenMaintenance();
      return;
    }
    updateTokenExpiryNotice();
  } catch {
    updateTokenExpiryNotice();
    renderAuthDiagnostics();
  }
}

// ─── Auth init (wiring button handlers) ─────

export async function initAuth(): Promise<void> {
  await ensureRuntimeConfigLoaded();
  applyRuntimeVisibility();
  setAuthState("none");

  document.getElementById("btn-sign-in")?.addEventListener("click", () => signIn());
  // "Change account" always shows the AAD account picker, even though the mailbox
  // account is already signed in -- see signIn(forceAccountPicker) / acquireBcToken.
  document.getElementById("btn-change-account")?.addEventListener("click", () => signIn(true));
  document.getElementById("btn-copy-diagnostics")?.addEventListener("click", () => {
    void copyDiagnosticsToClipboard();
  });
  document.getElementById("btn-refresh-diagnostics")?.addEventListener("click", () => {
    void refreshDiagnosticsPanel();
  });
  renderAuthDiagnostics();

  const token = await ensureAccessTokenCore(false);
  if (!token) {
    maybeShowSessionContinuityHint();
    updateAuthBanner(false);
    updateAuthActionButtons(false);
    showCompanySection(false);
    showCustomersSection(false);
    renderAuthDiagnostics();
    return;
  }

  // Token was restored from cache - apply UI updates
  setAuthState("ok");
  const account = getCurrentAccount();
  showAuthDetail(`Signed in as ${account?.username ?? "current user"}`);
  scheduleTokenMaintenance();
  updateAuthBanner(true);
  updateAuthActionButtons(true);
  const hasBcConfig = Boolean(loadSettingsCore().bcUrl);
  showCompanySection(hasBcConfig);
  showCustomersSection(hasBcConfig);

  if (onSignInSuccess) await onSignInSuccess();
  renderAuthDiagnostics();
}

// ─── Diagnostics ────────────────────────────

async function refreshDiagnosticsPanel(): Promise<void> {
  await ensureRuntimeConfigLoaded(true);
  renderAuthDiagnostics();
}

export function renderAuthDiagnostics(): void {
  const msalCacheLocation = getMsalCacheLocation();
  const msalInstance = getMsalInstanceRef();
  const currentAccount = getCurrentAccount();
  const tokenExpiresAtMs = getTokenExpiresAtMs();
  const lastTokenSource = getLastTokenSource();
  const lastAuthError = getLastAuthError();
  const settings = loadSettingsCore();

  const runtimeCache = stringifyCacheLocation(msalCacheLocation);
  const instanceCache = getInstanceCacheLocationLabel(msalInstance, msalCacheLocation);
  setDiagText("diag-token-cache", instanceCache === "not initialized" ? runtimeCache : instanceCache);
  setDiagText("diag-account", getDiagnosticAccountLabel(currentAccount, msalInstance));
  setDiagText("diag-token-expiry", formatTokenExpiry(tokenExpiresAtMs));
  setDiagText("diag-next-refresh", formatNextRefresh(tokenRefreshScheduledAtMs));
  setDiagText("diag-token-source", lastTokenSource);
  setDiagText("diag-redirect-uri", getMsalRedirectUri());
  setDiagText("diag-login-hint", formatDiagnosticValue(getLoginHintForDiagnostics() ?? "", "not available"));
  setDiagText("diag-account-count", getMsalAccountsCountLabel(msalInstance));
  setDiagText("diag-last-auth-error", lastAuthError || "none");
  setDiagText("diag-session-note", getAuthSessionNoteDiagnosticValue());
  setDiagText("diag-runtime-source", getRuntimeConfigSource());
  setDiagText("diag-config-mode", getUseTaskpaneConfiguration() ? "taskpane settings" : "runtime environment");
  setDiagText("diag-bc-endpoint", formatDiagnosticValue(settings.bcUrl, "not configured"));
  setDiagText("diag-client-id", formatClientIdDiagnostic(settings.clientId));
  setDiagText("diag-runtime-error", getRuntimeConfigLastError() || "none");
  setDiagText(
    "diag-runtime-loaded-at",
    getRuntimeConfigLoadedAtMs() ? new Date(getRuntimeConfigLoadedAtMs()).toLocaleString() : "n/a",
  );
  renderCacheMismatchWarning(runtimeCache, instanceCache);
}

function getLoginHintForDiagnostics(): string | undefined {
  try {
    return Office.context.mailbox?.userProfile?.emailAddress ?? undefined;
  } catch {
    return undefined;
  }
}

function formatNextRefresh(nextRefreshAtMs: number): string {
  if (!nextRefreshAtMs) return "not scheduled";
  const deltaMs = nextRefreshAtMs - Date.now();
  if (deltaMs <= 0) return "due now";
  const deltaMin = Math.max(1, Math.ceil(deltaMs / 60_000));
  return `${new Date(nextRefreshAtMs).toLocaleString()} (~${deltaMin}m)`;
}

function getAuthSessionNoteDiagnosticValue(): string {
  const el = document.getElementById("auth-session-note");
  const msg = (el?.textContent ?? "").trim();
  return msg || "none";
}

function setDiagText(elementId: string, value: string): void {
  const el = document.getElementById(elementId);
  if (el) el.textContent = value;
}

function setDiagCopyStatus(message: string): void {
  const el = document.getElementById("diag-copy-status");
  if (!el) return;
  el.textContent = message;
  if (!message) return;
  window.setTimeout(() => {
    if (el.textContent === message) el.textContent = "";
  }, 2200);
}

function buildDiagnosticsSnapshot(): string {
  const lines: string[] = [];
  lines.push("BC Outlook Add-in Diagnostics");
  lines.push(`Captured: ${new Date().toLocaleString()}`);
  lines.push(`Origin: ${window.location.origin}`);
  lines.push("");

  const grid = document.querySelector(".diagnostics-grid");
  if (grid) {
    const keys = Array.from(grid.querySelectorAll(".diag-k"));
    keys.forEach((k, idx) => {
      const key = (k.textContent ?? "").trim();
      const value = (grid.querySelectorAll(".diag-v")[idx]?.textContent ?? "").trim();
      if (key) lines.push(`${key}: ${value || "-"}`);
    });
  }

  const mismatch = (document.getElementById("diag-cache-mismatch")?.textContent ?? "").trim();
  if (mismatch) lines.push(`Cache warning: ${mismatch}`);

  return lines.join("\n");
}

async function copyDiagnosticsToClipboard(): Promise<void> {
  const text = buildDiagnosticsSnapshot();
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      setDiagCopyStatus("Diagnostics copied.");
      return;
    }
  } catch {
    // Fallback below
  }

  const area = document.createElement("textarea");
  area.value = text;
  area.style.position = "fixed";
  area.style.left = "-9999px";
  document.body.appendChild(area);
  area.focus();
  area.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(area);
  setDiagCopyStatus(ok ? "Diagnostics copied." : "Copy failed.");
}

function getInstanceCacheLocationLabel(
  msalInstance: import("@azure/msal-browser").PublicClientApplication | null,
  fallbackCacheLocation: BrowserCacheLocation,
): string {
  if (!msalInstance) return "not initialized";
  const cacheLocation = (msalInstance as any)?.getConfiguration?.()?.cache?.cacheLocation;
  return stringifyCacheLocation(cacheLocation ?? fallbackCacheLocation);
}

function renderCacheMismatchWarning(runtimeCache: string, instanceCache: string): void {
  const el = document.getElementById("diag-cache-mismatch");
  if (!el) return;

  const hasMismatch =
    instanceCache !== "not initialized" &&
    instanceCache !== "unknown" &&
    runtimeCache !== "unknown" &&
    runtimeCache !== instanceCache;
  if (!hasMismatch) {
    el.style.display = "none";
    el.textContent = "";
    return;
  }

  el.style.display = "block";
  el.textContent = `Warning: runtime policy is ${runtimeCache}, but MSAL instance uses ${instanceCache}. Reopen task pane or sign out/in to reinitialize.`;
}

function stringifyCacheLocation(value: string | BrowserCacheLocation): string {
  switch (value) {
    case BrowserCacheLocation.SessionStorage:
      return "sessionStorage";
    case BrowserCacheLocation.LocalStorage:
      return "localStorage";
    case BrowserCacheLocation.MemoryStorage:
      return "memoryStorage";
    default:
      return String(value || "unknown");
  }
}

function getDiagnosticAccountLabel(
  currentAccount: import("@azure/msal-browser").AccountInfo | null,
  msalInstance: import("@azure/msal-browser").PublicClientApplication | null,
): string {
  const account = currentAccount ?? msalInstance?.getActiveAccount() ?? null;
  return account?.username || account?.name || "not selected";
}

function getMsalAccountsCountLabel(
  msalInstance: import("@azure/msal-browser").PublicClientApplication | null,
): string {
  if (!msalInstance) return "n/a (msal not initialized)";
  return String(msalInstance.getAllAccounts().length);
}

function formatTokenExpiry(expiresAtMs: number): string {
  if (!expiresAtMs) return "no token";
  const deltaMs = expiresAtMs - Date.now();
  const deltaMin = Math.max(0, Math.floor(deltaMs / 60_000));
  return `${new Date(expiresAtMs).toLocaleString()} (${deltaMin}m left)`;
}

function formatDiagnosticValue(value: string, fallback: string): string {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
}

function formatClientIdDiagnostic(clientId: string): string {
  const normalized = String(clientId ?? "").trim();
  if (!normalized) return "not configured";
  if (normalized.length <= 12) return normalized;
  return `${normalized.slice(0, 8)}...${normalized.slice(-4)}`;
}

function getMsalRedirectUri(): string {
  return buildAuthRedirectUri(window.location.origin);
}
